/*
 * Lemonade Training Oy, copy rights, all right reserved.
 */

// Private windows and blocked site data throw on localStorage rather than
// returning null, so every access goes through here.
const guarded = (read, fallback) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return fallback;
    return read(window.localStorage);
  } catch {
    return fallback;
  }
};

/**
 * A device-local echo of one preference, stored as exactly `{ uid, [field] }`
 * so nothing else on the settings document can leak into the browser.
 */
export const createPreferenceMirror = ({ key, field, isValid, fallback }) => {
  const parse = (raw) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      if (!isValid(parsed[field])) return null;
      return { uid: typeof parsed.uid === "string" ? parsed.uid : null, value: parsed[field] };
    } catch {
      return null;
    }
  };

  return {
    /** Read synchronously so the first render paints the user's choice, not the default. */
    read: () => guarded((storage) => parse(storage.getItem(key))?.value, undefined) ?? fallback,
    /**
     * Who the mirrored value belongs to, or `null` if unknown. The first paint
     * happens before any uid is available, so whoever learns the uid discards a
     * stranger's value.
     */
    readUid: () => guarded((storage) => parse(storage.getItem(key))?.uid ?? null, null),
    write: (value, uid) =>
      guarded((storage) => {
        if (!isValid(value)) return;
        storage.setItem(
          key,
          JSON.stringify({ uid: typeof uid === "string" ? uid : null, [field]: value })
        );
      }),
    clear: () => guarded((storage) => storage.removeItem(key)),
  };
};
